import "./FeatureModal.css";

/* TODO: Update MDS Modal to HeadlessUI Dialog, import from there */
// eslint-disable-next-line no-restricted-imports
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
  CloseButton as HeadlessCloseButton,
} from "@headlessui/react";
import clsx from "clsx";
import { type FC } from "react";

import { CloseButton } from "../../../../mds/components/CloseButton";
import { blockIsAsset, getEditableBlocks } from "../../../../shared/worksheets/doc-helpers";
import { useAv } from "../../../../utils/av";

import { FeatureHeader } from "./header/FeatureHeader";

import { DisplayCaption } from "components/DisplayCaption";
import {
  FOLLOWING_QUERY_PARAM,
  FOOTER_SECONDARY_OVERLAYS,
  SECONDARY_OVERLAY_QUERY_PARAM,
} from "components/constants";
import { PageDetailView } from "components/materials/page/content/PageDetailView";
import { FeatureFooter } from "components/materials/presentation/featuring/FeatureFooter";
import { FeatureOutcomes } from "components/materials/presentation/featuring/FeatureOutcomes";
import { ProjectorScroller } from "components/materials/presentation/projector/ProjectorScroller";
import { useFetchGroupData } from "mds/hooks/use-fetch-group-data";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { useWorksheet } from "sharedb/ShareDBDoc";
import { useAppDispatch, useAppSelector } from "store/index";
import {
  pageSelectors,
  selectPreventFeatureModalClosing,
  selectProjectorHeight,
} from "store/selectors";
import { viewStateActions } from "store/slices/view";
import {
  PROJECTOR_MAX_HEIGHT,
  stopFeaturing,
  usePresentation,
  usePresentationCaption,
} from "utils/presentation";
import { useQueryParamsManager } from "utils/routing";

const calculateModalMaxHeight = (
  projectorHeight: number | null,
  isFeaturingAsset: boolean,
  isMobile: boolean,
  isInVirtualClass: boolean,
) => {
  // We want the viewport height of the feature modal to be no larger than the projector viewport height.
  // However if the modal is already smaller than that we don't want to increase the height.
  // --participant-bar-height is 0 when not in virtual class, so we can always subtract it.
  // In a virtual class on mobile, we don't want any padding so it's the exact height of the content area.
  const padding = isMobile && isInVirtualClass ? "0px" : isFeaturingAsset ? "30px" : "60px";

  return `min(calc(100vh - ${padding} - var(--navbar-height) - ${isMobile ? "var(--footer-height-mobile)" : "var(--footer-height)"} - var(--participant-bar-height)), ${projectorHeight}px)`;
};

export const FeatureModal: FC = () => {
  const {
    presentedSessionId,
    presentedPageId: pageId,
    isPresenting,
    isPresentingUser,
    presentedBlockId,
    presentedAccessId,
    presentingOutcomes,
    presentingRollup,
  } = usePresentation();
  const dispatch = useAppDispatch();
  const projectorHeight = useAppSelector(selectProjectorHeight) || PROJECTOR_MAX_HEIGHT;
  const { captionText, captionBgColor } = usePresentationCaption();
  const page = useAppSelector((s) => pageSelectors.selectById(s, pageId));
  const { deleteQueryParams, getQueryParam } = useQueryParamsManager();
  const preventFeatureModalClosing = useAppSelector(selectPreventFeatureModalClosing);
  const currentOverlay = getQueryParam(SECONDARY_OVERLAY_QUERY_PARAM);
  const isMobile = !useIsMdOrLarger();
  const { isInVirtualClass } = useAv();
  const isInVirtualClassOnMobile = isInVirtualClass && isMobile;

  const { doc } = useWorksheet(page?.worksheet_id);
  const isStaticContent = Boolean(!doc || !getEditableBlocks(doc)?.length);
  const isFeaturingAsset = Boolean(presentedBlockId && doc && blockIsAsset(doc, presentedBlockId));

  const showingFooterSecondaryOverlay =
    !!currentOverlay && FOOTER_SECONDARY_OVERLAYS.includes(currentOverlay);

  const interactionMode = page?.interaction_mode;

  const modalMaxHeight = calculateModalMaxHeight(
    isFeaturingAsset ? PROJECTOR_MAX_HEIGHT : projectorHeight,
    isFeaturingAsset,
    isMobile,
    isInVirtualClass,
  );

  // Load the page groups, page section users depending on the type of page
  useFetchGroupData(pageId, interactionMode);

  const onClose = () => {
    // Let presenting user use the certain features (like presentation controls) without closing the modal
    if (isPresentingUser && preventFeatureModalClosing) {
      dispatch(viewStateActions.setPreventFeatureModalClosing(false));

      return false;
    }

    if (isPresenting && presentedSessionId) {
      stopFeaturing(presentedSessionId);
    } else {
      deleteQueryParams([FOLLOWING_QUERY_PARAM]);
    }

    dispatch(viewStateActions.clearFeaturingPdfState());
  };

  if (!page || !doc) {
    return null;
  }
  return (
    <Dialog
      className={clsx("feature-modal relative z-50", {
        "feature-modal-with-asset": isFeaturingAsset,
      })}
      open
      onClose={() => {}}
    >
      <div
        className={clsx(
          "fixed inset-x-0 top-[calc(var(--navbar-height)+var(--participant-bar-height))] z-50 md:data-[virtual-class]:inset-x-3",
          isMobile ? "bottom-[--footer-height-mobile]" : "bottom-[--footer-height]",
        )}
        data-virtual-class={isInVirtualClass || undefined}
        style={
          !isMobile && showingFooterSecondaryOverlay
            ? // The page secondary sidebar takes up 1/3 of the screen width, and has a
              // min width of 392px and max width of 520px (plus an extra 12px of padding
              // on the right for virtual classes).
              // This calculates how much the featured content modal needs to shift in order
              // to show it in the "middle" of the screen when the secondary sidebar is open.
              { right: `min(max(100vw / 3, 392px), ${isInVirtualClass ? "532" : "520"}px)` }
            : undefined
        }
      >
        {/* / We don't blur the background here. Instead, we blur the page sidebar, the page content, and the background of the page secondary sidebar. */}
        <DialogBackdrop className="absolute inset-0" />

        <div className="absolute left-0 right-0 top-0 flex h-full w-full items-center justify-center">
          <div
            className={clsx(
              "flex flex-col overflow-hidden rounded-lg border-2 border-green-tint-20 bg-white",
              isInVirtualClassOnMobile
                ? "w-[calc(100%-24px)]" // For virtual classes on mobile, we want the feature modal to take up the full width of the screen minus 24px of padding
                : isFeaturingAsset
                  ? "w-full max-w-[calc(100%-20px)] md:max-w-[calc(100%-120px)]"
                  : "w-[calc(min(calc(100%-20px),var(--page-default-max-width)+2*40px))] md:w-[calc(var(--page-default-max-width)+2*40px)]",
            )}
            id="feature-modal-container"
            style={{
              maxHeight: modalMaxHeight,
              minHeight: isInVirtualClassOnMobile ? modalMaxHeight : undefined,
            }}
          >
            <DisplayCaption bgColorClass={captionBgColor} caption={captionText} />

            <DialogPanel className="flex flex-col gap-2 overflow-hidden p-1.5">
              <div
                className={clsx(
                  "flex min-h-[56px] justify-between gap-2 pl-2",
                  isFeaturingAsset ? "md:pl-4" : "md:pl-8",
                )}
              >
                <DialogTitle className="body-s w-full pt-[26px] text-base font-medium">
                  <FeatureHeader doc={doc} page={page} />
                </DialogTitle>

                <div className={isFeaturingAsset ? "pr-2 pt-[26px] md:pr-4" : ""}>
                  <HeadlessCloseButton
                    as={CloseButton}
                    eventName="Feature Modal - Close"
                    onClick={onClose}
                  />
                </div>
              </div>

              <ProjectorScroller
                className={clsx({ flex: isFeaturingAsset })}
                id="feature-modal-scroller"
                isInVirtualClass={isInVirtualClass}
                pageId={page?.id}
                isFeatureModal
              >
                {presentingOutcomes ? (
                  <FeatureOutcomes page={page} />
                ) : (
                  <PageDetailView
                    isStaticContent={isStaticContent}
                    page={page}
                    selectedAccessId={presentedAccessId}
                    selectedTabVariant={presentingRollup ? "responses" : undefined}
                    isFeaturing
                  />
                )}
              </ProjectorScroller>

              <FeatureFooter doc={doc} page={page} />
            </DialogPanel>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
